import {
  DURATION,
  SPRING_SHEET,
  animateSpring,
  prefersReducedMotion,
  project,
  rubberband,
} from "@/lib/motion";
import {
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

/**
 * ui.tsx - jw-pds 프리미티브
 *
 * 스타일은 전부 토큰(Tailwind alias 또는 var(--…)) 을 통해 나간다.
 * 이 파일에 raw hex 는 없다 (lint:ds:grep 가 막는다).
 *
 * API 형태는 jw-cms 의 ui.tsx 를 따라간다. 두 제품 사이에서 화면
 * 코드가 이식되도록. 스타일 레이어만 통째로 갈렸다.
 *
 * hover / active 는 useState 가 아니라 CSS 로 처리한다. 리렌더가
 * 없고, :active 는 포인터 다운 순간에 걸리므로 apple-design 1
 * ("피드백은 릴리즈가 아니라 누르는 순간") 이 공짜로 지켜진다.
 */

/* =====================================================================
   Icon - 24x24 스트로크 라인 아이콘
   외부 아이콘 라이브러리를 쓰지 못한다(의존성 0 제약). 대신 형제
   제품 jw-cms 와 같은 세트를 공유해서 두 제품의 아이콘이 어긋나지
   않게 한다. strokeWidth 는 전역 2 로 고정한다.
   ===================================================================== */

export type IconName =
  | "home"
  | "grid"
  | "list"
  | "plus"
  | "chart"
  | "db"
  | "eye"
  | "message"
  | "search"
  | "user"
  | "users"
  | "chevL"
  | "chevR"
  | "chevD"
  | "chevU"
  | "download"
  | "file"
  | "paperclip"
  | "check"
  | "x"
  | "alert"
  | "filter"
  | "calendar"
  | "clock"
  | "history"
  | "arrowRight"
  | "arrowUp"
  | "arrowDown"
  | "save"
  | "logout"
  | "settings"
  | "trash"
  | "sun"
  | "moon"
  | "menu"
  | "external"
  | "dot";

const PATHS: Record<IconName, ReactNode> = {
  home: (
    <>
      <path d="M3 12 12 3l9 9" />
      <path d="M5 10v10h14V10" />
    </>
  ),
  grid: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </>
  ),
  list: (
    <>
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </>
  ),
  plus: (
    <>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </>
  ),
  chart: (
    <>
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </>
  ),
  db: (
    <>
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
      <path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  message: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21v-1a7 7 0 0 1 14 0v1" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.4" />
      <path d="M3 21v-1a6 6 0 0 1 12 0v1" />
      <path d="M16 5.2a3.4 3.4 0 0 1 0 5.6" />
      <path d="M18 14.4A6 6 0 0 1 21 20v1" />
    </>
  ),
  chevL: <path d="m15 18-6-6 6-6" />,
  chevR: <path d="m9 18 6-6-6-6" />,
  chevD: <path d="m6 9 6 6 6-6" />,
  chevU: <path d="m6 15 6-6 6 6" />,
  download: (
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M7 10l5 5 5-5" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </>
  ),
  file: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </>
  ),
  paperclip: (
    <path d="M21.44 11.05l-9.19 9.19a5 5 0 0 1-7.07-7.07l9.19-9.19a3.5 3.5 0 0 1 4.95 4.95l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  ),
  check: <path d="M20 6 9 17l-5-5" />,
  x: (
    <>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </>
  ),
  alert: (
    <>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </>
  ),
  filter: <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />,
  calendar: (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  history: (
    <>
      <path d="M3 3v5h5" />
      <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
      <path d="M12 7v5l4 2" />
    </>
  ),
  arrowRight: (
    <>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </>
  ),
  arrowUp: (
    <>
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </>
  ),
  arrowDown: (
    <>
      <line x1="12" y1="5" x2="12" y2="19" />
      <polyline points="19 12 12 19 5 12" />
    </>
  ),
  save: (
    <>
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </>
  ),
  logout: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </>
  ),
  trash: (
    <>
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </>
  ),
  moon: <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9z" />,
  menu: (
    <>
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </>
  ),
  external: (
    <>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </>
  ),
  dot: <circle cx="12" cy="12" r="4" />,
};

export const Icon = ({
  name,
  size = 16,
  stroke = 2,
  color = "currentColor",
  style,
}: {
  name: IconName;
  size?: number;
  stroke?: number;
  color?: string;
  style?: CSSProperties;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0, ...style }}
    aria-hidden="true"
  >
    {PATHS[name]}
  </svg>
);

/* =====================================================================
   Num - 숫자 표기. 예외 없이 tabular-nums.
   ===================================================================== */

export const Num = ({
  v,
  suffix,
  strong,
  style,
}: {
  v: number;
  suffix?: string;
  strong?: boolean;
  style?: CSSProperties;
}) => (
  <span
    data-num=""
    className="whitespace-nowrap tabular-nums"
    style={{ fontWeight: strong ? 700 : undefined, ...style }}
  >
    {v.toLocaleString("ko-KR")}
    {suffix && <span style={{ fontWeight: 400 }}>{suffix}</span>}
  </span>
);

/* =====================================================================
   Button
   disabled 를 opacity 로 처리하지 않는다. 흐려진 글자는 대비를
   깨뜨린다. 대신 표면을 inset 으로 내리고 잉크를 quaternary 로
   바꾼다 (4.05:1 라이트 / 3.66:1 다크 - 비활성 컨트롤이므로
   WCAG 1.4.3 예외 범위이지만 그래도 3:1 을 넘긴다).
   ===================================================================== */

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "link";
export type ButtonSize = "sm" | "md" | "lg";

const BTN_SIZE: Record<ButtonSize, string> = {
  sm: "h-control-sm px-s3 gap-s1 text-caption",
  md: "h-control-md px-s4 gap-s2 text-label",
  lg: "h-control-lg px-s5 gap-s2 text-body",
};

const BTN_STYLE: Record<ButtonVariant, CSSProperties> = {
  primary: { background: "var(--accent)", color: "var(--text-on-accent)" },
  secondary: {
    background: "var(--surface)",
    color: "var(--text-primary)",
    boxShadow: "inset 0 0 0 1px var(--border-strong)",
  },
  ghost: { background: "transparent", color: "var(--text-secondary)" },
  danger: { background: "var(--state-late)", color: "var(--text-on-accent)" },
  link: { background: "transparent", color: "var(--accent)" },
};

const BTN_HOVER: Record<ButtonVariant, string> = {
  primary: "hover:bg-accent-hover active:bg-accent-press",
  secondary: "hover:bg-overlay-hover active:bg-overlay-press",
  ghost: "hover:bg-overlay-hover active:bg-overlay-press hover:text-primary",
  danger: "hover:brightness-110 active:brightness-95",
  link: "hover:underline underline-offset-2",
};

export const Button = ({
  variant = "secondary",
  size = "md",
  icon,
  iconEnd,
  loading = false,
  disabled = false,
  fullWidth = false,
  type = "button",
  onClick,
  children,
  className,
  style,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  iconEnd?: ReactNode;
  /** 라벨을 유지한 채 스피너를 얹는다. 레이아웃이 흔들리지 않는다. */
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  type?: "button" | "submit" | "reset";
  onClick?: () => void;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
}) => {
  const off = disabled || loading;
  return (
    <button
      type={type}
      onClick={off ? undefined : onClick}
      disabled={off}
      aria-busy={loading || undefined}
      className={[
        "inline-flex items-center justify-center whitespace-nowrap rounded-control font-semibold",
        "transition-[background-color,box-shadow,transform,filter] duration-press ease-standard",
        "active:scale-[0.98] disabled:active:scale-100 disabled:cursor-not-allowed",
        BTN_SIZE[size],
        off ? "" : BTN_HOVER[variant],
        fullWidth ? "w-full" : "",
        className ?? "",
      ].join(" ")}
      style={
        off
          ? {
              background:
                variant === "ghost" || variant === "link" ? "transparent" : "var(--surface-inset)",
              color: "var(--text-quaternary)",
              boxShadow:
                variant === "secondary" ? "inset 0 0 0 1px var(--border-subtle)" : undefined,
              ...style,
            }
          : { ...BTN_STYLE[variant], ...style }
      }
    >
      {loading ? <Spinner size={size === "lg" ? 16 : 14} /> : icon}
      {children}
      {iconEnd}
    </button>
  );
};

/** jw-cms 화면 코드 이식용 별칭. 새 코드에서는 Button 을 쓴다. */
export const Pill = Button;

export const Spinner = ({ size = 14 }: { size?: number }) => (
  <span
    aria-hidden="true"
    className="inline-block shrink-0 animate-spin rounded-pill"
    style={{
      width: size,
      height: size,
      border: "2px solid currentColor",
      borderTopColor: "transparent",
      opacity: 0.85,
    }}
  />
);

/* =====================================================================
   Card / SectionTitle
   카드는 위계가 실제로 있을 때만 쓴다. 밀도 8 에서는 대부분
   1px 헤어라인과 여백으로 묶는 편이 낫다.
   ===================================================================== */

export const Card = ({
  children,
  pad = "md",
  className,
  style,
}: {
  children: ReactNode;
  pad?: "none" | "sm" | "md";
  className?: string;
  style?: CSSProperties;
}) => (
  <div
    className={[
      "rounded-card",
      pad === "md" ? "p-s5" : pad === "sm" ? "p-s4" : "",
      className ?? "",
    ].join(" ")}
    style={{ background: "var(--surface)", boxShadow: "var(--shadow-raised)", ...style }}
  >
    {children}
  </div>
);

export const SectionTitle = ({
  children,
  sub,
  right,
}: {
  children: ReactNode;
  sub?: string;
  right?: ReactNode;
}) => (
  <div className="mb-s4 flex flex-wrap items-end justify-between gap-s3">
    <div>
      <h2 className="text-title">{children}</h2>
      {sub && (
        <p className="mt-s1 text-caption" style={{ color: "var(--text-tertiary)" }}>
          {sub}
        </p>
      )}
    </div>
    {right}
  </div>
);

/* =====================================================================
   Field - 라벨은 위, 에러는 아래. 예외 없다.
   placeholder 를 라벨로 쓰는 일은 절대 없다. placeholder 는
   형식 힌트("YYYY-MM-DD") 전용이다.

   children 에 함수를 주면 {id, "aria-describedby", "aria-invalid"} 를
   받아 컨트롤에 그대로 펼칠 수 있다.
   ===================================================================== */

export type FieldChildProps = {
  id: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
};

export const Field = ({
  label,
  required = false,
  hint,
  error,
  children,
  className,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  /** 있으면 hint 를 대체하고 컨트롤이 invalid 상태가 된다 */
  error?: string;
  children: ReactNode | ((p: FieldChildProps) => ReactNode);
  className?: string;
}) => {
  const id = useId();
  const msgId = `${id}-msg`;
  const msg = error ?? hint;
  const wired = typeof children === "function";
  const childProps: FieldChildProps = {
    id,
    "aria-describedby": msg ? msgId : undefined,
    "aria-invalid": error ? true : undefined,
  };
  return (
    <div className={["flex flex-col gap-s2", className ?? ""].join(" ")}>
      <label
        // children 이 함수일 때만 id 가 실제 컨트롤에 꽂힌다. 평범한
        // 노드(체크박스 묶음 등)에 htmlFor 를 걸면 존재하지 않는 id 를
        // 가리켜서 아예 라벨이 없는 것보다 나쁘다.
        htmlFor={wired ? id : undefined}
        className="text-caption font-semibold"
        style={{ color: "var(--text-secondary)" }}
      >
        {label}
        {required && (
          <span style={{ color: "var(--state-late)" }} aria-hidden="true">
            {" *"}
          </span>
        )}
        {required && <span className="sr-only">(필수)</span>}
      </label>
      {wired ? children(childProps) : children}
      {msg && (
        <span
          id={msgId}
          className="flex items-center gap-s1 text-micro"
          style={{ color: error ? "var(--state-late)" : "var(--text-tertiary)" }}
        >
          {error && <Icon name="alert" size={12} />}
          {msg}
        </span>
      )}
    </div>
  );
};

/* =====================================================================
   입력 컨트롤 - 전부 네이티브다.
   테두리는 --border-strong (표면 대비 3:1 이상, WCAG 1.4.11).
   포커스 링은 globals.css 의 전역 :focus-visible 이 그린다.
   ===================================================================== */

const CONTROL =
  "w-full h-control-md rounded-control px-s4 text-label transition-[box-shadow] duration-fast ease-standard";

const controlStyle = (invalid?: boolean): CSSProperties => ({
  background: "var(--surface-inset)",
  color: "var(--text-primary)",
  border: `1px solid ${invalid ? "var(--state-late)" : "var(--border-strong)"}`,
});

export const TextInput = ({
  value,
  onChange,
  placeholder,
  type = "text",
  align = "left",
  disabled = false,
  maxLength,
  id,
  className,
  style,
  ariaLabel,
  ...aria
}: {
  value: string;
  onChange?: (v: string) => void;
  /** 형식 힌트 전용. 라벨 대체 금지. */
  placeholder?: string;
  type?: "text" | "number" | "email" | "password" | "tel" | "search";
  align?: "left" | "right" | "center";
  disabled?: boolean;
  maxLength?: number;
  id?: string;
  className?: string;
  style?: CSSProperties;
  /**
 * `Field` 로 감싸지 않은 컨트롤의 접근가능한 이름.
 * placeholder 는 형식 힌트 전용이므로 라벨을 대신할 수 없다
 * (디자인시스템 §10-8). FilterBar 처럼 보이는 라벨을 둘 자리가 없는 곳에서
 * 쓴다. `Field` 안에서는 필요 없다 - `htmlFor` 가 이미 이름을 준다.
 */
  ariaLabel?: string;
} & Partial<FieldChildProps>) => (
  <input
    id={id}
    type={type}
    value={value}
    placeholder={placeholder}
    disabled={disabled}
    maxLength={maxLength}
    aria-label={ariaLabel}
    onChange={(e) => onChange?.(e.target.value)}
    data-num={type === "number" || align === "right" ? "" : undefined}
    className={[CONTROL, className ?? ""].join(" ")}
    style={{
      ...controlStyle(aria["aria-invalid"]),
      textAlign: align,
      ...(disabled ? { color: "var(--text-quaternary)", cursor: "not-allowed" } : {}),
      ...style,
    }}
    aria-describedby={aria["aria-describedby"]}
    aria-invalid={aria["aria-invalid"]}
  />
);

export const Textarea = ({
  value,
  onChange,
  placeholder,
  rows = 3,
  maxLength,
  disabled = false,
  id,
  ariaLabel,
  ...aria
}: {
  value: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  disabled?: boolean;
  id?: string;
  ariaLabel?: string;
} & Partial<FieldChildProps>) => (
  <textarea
    id={id}
    value={value}
    rows={rows}
    placeholder={placeholder}
    maxLength={maxLength}
    disabled={disabled}
    aria-label={ariaLabel}
    onChange={(e) => onChange?.(e.target.value)}
    className="w-full resize-y rounded-control px-s4 py-s3 text-label"
    style={controlStyle(aria["aria-invalid"])}
    aria-describedby={aria["aria-describedby"]}
    aria-invalid={aria["aria-invalid"]}
  />
);

export type SelectOption = string | { value: string; label: string };

export const Select = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  id,
  className,
  ariaLabel,
  ...aria
}: {
  value: string;
  onChange?: (v: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  ariaLabel?: string;
} & Partial<FieldChildProps>) => (
  <span className={["relative inline-flex w-full items-center", className ?? ""].join(" ")}>
    <select
      id={id}
      value={value}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(e) => onChange?.(e.target.value)}
      className={`${CONTROL} cursor-pointer appearance-none pr-s7`}
      style={{
        ...controlStyle(aria["aria-invalid"]),
        ...(disabled ? { color: "var(--text-quaternary)", cursor: "not-allowed" } : {}),
      }}
      aria-describedby={aria["aria-describedby"]}
      aria-invalid={aria["aria-invalid"]}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.value;
        const l = typeof o === "string" ? o : o.label;
        return (
          <option key={v} value={v}>
            {l}
          </option>
        );
      })}
    </select>
    <span
      aria-hidden="true"
      className="pointer-events-none absolute right-s4 flex"
      style={{ color: "var(--text-tertiary)" }}
    >
      <Icon name="chevD" size={14} />
    </span>
  </span>
);

/**
 * DateField - 네이티브 <input type="date">.
 * 라이브러리를 쓰지 않는 이유: (1) 의존성 0, (2) 사용자가 이미 아는
 * 컨트롤이고 모바일에서 OS 피커가 뜬다 (apple-design 16.4 familiarity).
 * 표시 형식은 브라우저 로케일이 정한다. 앱이 문자열을 만들지 않는다.
 */
export const DateField = ({
  value,
  onChange,
  onBlur,
  min,
  max,
  disabled = false,
  id,
  ariaLabel,
  ...aria
}: {
  /** YYYY-MM-DD */
  value: string;
  onChange?: (v: string) => void;
  /**
   * 포커스를 잃는 순간의 값. 계획일은 저장 버튼 없이 여기서 PATCH 한다
   * (결정 S-9: §2 의 "1건당 1분 이내" 예산에서 저장 버튼은 클릭 1회를 더한다).
   */
  onBlur?: (v: string) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  id?: string;
  ariaLabel?: string;
} & Partial<FieldChildProps>) => (
  <input
    id={id}
    type="date"
    value={value}
    min={min}
    max={max}
    disabled={disabled}
    aria-label={ariaLabel}
    onChange={(e) => onChange?.(e.target.value)}
    onBlur={onBlur ? (e) => onBlur(e.target.value) : undefined}
    data-num=""
    className={CONTROL}
    style={{
      ...controlStyle(aria["aria-invalid"]),
      ...(disabled ? { color: "var(--text-quaternary)", cursor: "not-allowed" } : {}),
    }}
    aria-describedby={aria["aria-describedby"]}
    aria-invalid={aria["aria-invalid"]}
  />
);

/**
 * Checkbox / Radio - 네이티브 input + accent-color.
 * 커스텀 렌더링을 하지 않으므로 키보드, 스크린리더, 고대비 모드,
 * forced-colors 가 전부 공짜로 맞는다.
 */
export const Checkbox = ({
  checked,
  onChange,
  label,
  disabled = false,
  indeterminate = false,
}: {
  checked: boolean;
  onChange?: (v: boolean) => void;
  label: ReactNode;
  disabled?: boolean;
  indeterminate?: boolean;
}) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <label
      className={[
        "inline-flex cursor-pointer select-none items-center gap-s2 text-label",
        disabled ? "cursor-not-allowed" : "",
      ].join(" ")}
      style={{ color: disabled ? "var(--text-quaternary)" : "var(--text-primary)" }}
    >
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        className="h-4 w-4 shrink-0 cursor-pointer"
        style={{ accentColor: "var(--accent)" }}
      />
      {label}
    </label>
  );
};

export const Radio = ({
  name,
  value,
  checked,
  onChange,
  label,
  disabled = false,
}: {
  name: string;
  value: string;
  checked: boolean;
  onChange?: (v: string) => void;
  label: ReactNode;
  disabled?: boolean;
}) => (
  <label
    className="inline-flex cursor-pointer select-none items-center gap-s2 text-label"
    style={{ color: disabled ? "var(--text-quaternary)" : "var(--text-primary)" }}
  >
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      disabled={disabled}
      onChange={() => onChange?.(value)}
      className="h-4 w-4 shrink-0 cursor-pointer"
      style={{ accentColor: "var(--accent)" }}
    />
    {label}
  </label>
);

/* =====================================================================
   Chip / Tag - 필터 토글과 읽기전용 태그
   ===================================================================== */

export const Chip = ({
  children,
  active = false,
  onClick,
  count,
  icon,
}: {
  children: ReactNode;
  active?: boolean;
  onClick?: () => void;
  count?: number;
  icon?: ReactNode;
}) => {
  if (!onClick) {
    return (
      <span
        className="inline-flex items-center gap-s1 rounded-pill px-s3 py-s05 text-caption font-medium"
        style={{ background: "var(--surface-inset)", color: "var(--text-secondary)" }}
      >
        {icon}
        {children}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="inline-flex items-center gap-s1 rounded-pill px-s3 py-s05 text-caption font-semibold transition-colors duration-fast ease-standard active:scale-[0.97]"
      style={
        active
          ? { background: "var(--accent)", color: "var(--text-on-accent)" }
          : {
              background: "var(--surface-inset)",
              color: "var(--text-secondary)",
              boxShadow: "inset 0 0 0 1px var(--border-subtle)",
            }
      }
    >
      {icon}
      {children}
      {count !== undefined && <span className="tabular-nums">{count}</span>}
    </button>
  );
};

/* =====================================================================
   ProgressBar / DDayChip - 화면마다 나오는 두 파생 숫자
   두 개 다 "숫자가 먼저, 그래픽은 보조" 다. 32행 테이블에서
   막대 32개가 나란히 뛰면 숫자를 못 읽는다.
   ===================================================================== */

export const ProgressBar = ({
  value,
  size = "row",
  label = "진행률",
}: {
  /** 0..100 */
  value: number;
  size?: "row" | "hero";
  label?: string;
}) => {
  const v = Math.max(0, Math.min(100, Math.round(value)));
  const done = v >= 100;
  const hero = size === "hero";
  return (
    // 접근성 값을 role="progressbar" 로 노출하지 않는다. 진짜 값은
    // 이미 텍스트("55%")로 렌더되어 있고, 막대는 그 숫자를 되풀이하는
    // 장식일 뿐이다. 장식에 interactive role 을 붙이면 키보드
    // 사용자에게 포커스 정지점만 늘어난다.
    <span className={hero ? "flex flex-col gap-s2" : "flex flex-col items-end gap-s05"}>
      <span
        className={
          hero ? "text-title font-bold tabular-nums" : "text-label font-semibold tabular-nums"
        }
        style={{ color: done ? "var(--state-done)" : "var(--text-primary)" }}
      >
        <span className="sr-only">{`${label} `}</span>
        {v}%
      </span>
      <span
        aria-hidden="true"
        className="block w-full overflow-hidden rounded-pill"
        style={{ height: hero ? 6 : 2, background: "var(--border-subtle)" }}
      >
        <span
          className="block h-full rounded-pill transition-[width] duration-base ease-out"
          style={{ width: `${v}%`, background: done ? "var(--state-done)" : "var(--accent)" }}
        />
      </span>
    </span>
  );
};

/**
 * DDayChip - SOP 까지 남은 일수.
 *   days >= 0 -> "D-47"   days < 0 -> "D+5" (초과)
 * 부호를 글자로 쓰기 때문에 색이 없어도 초과인지 알 수 있다.
 * 임계값 30 은 계획서 15/16 의 "SOP D-30" 에서 온다.
 */
export const DDayChip = ({
  days,
  size = "row",
}: {
  days: number;
  size?: "row" | "hero";
}) => {
  const over = days < 0;
  const near = !over && days <= 30;
  const text = over ? `D+${Math.abs(days)}` : `D-${days}`;
  const color = over ? "var(--state-late)" : near ? "var(--state-warn)" : "var(--text-secondary)";
  const bg = over
    ? "var(--state-late-soft)"
    : near
      ? "var(--state-warn-soft)"
      : "var(--surface-inset)";
  return (
    <span
      data-state-badge={over || near ? "" : undefined}
      className={[
        "inline-flex items-center gap-s1 rounded-pill font-semibold tabular-nums whitespace-nowrap",
        size === "hero" ? "px-s4 py-s1 text-heading" : "px-s2 py-px text-caption",
      ].join(" ")}
      style={{ background: bg, color }}
      aria-label={over ? `SOP ${Math.abs(days)}일 초과` : `SOP ${days}일 남음`}
    >
      {(over || near) && <Icon name="alert" size={size === "hero" ? 14 : 11} />}
      <span aria-hidden="true">{text}</span>
    </span>
  );
};

/* =====================================================================
   Tabs - 단일 페이지 탭 라우팅용 (react-router 없음)
   화살표 키 이동 + roving tabIndex. 활성 표시는 각 탭이 자기
   2px 밑줄을 갖고 색만 전환한다. 슬라이딩 인디케이터는 폭 측정이
   필요해서 안 쓴다 (ResizeObserver 없이는 리사이즈에서 어긋난다).
   ===================================================================== */

export type TabItem = { key: string; label: string; icon?: IconName; badge?: number };

export const Tabs = ({
  items,
  active,
  onChange,
  label,
}: {
  items: TabItem[];
  active: string;
  onChange: (key: string) => void;
  label: string;
}) => {
  const onKey = (e: React.KeyboardEvent) => {
    const i = items.findIndex((t) => t.key === active);
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const next = (i + (e.key === "ArrowRight" ? 1 : items.length - 1)) % items.length;
      onChange(items[next].key);
    }
  };
  return (
    <div
      role="tablist"
      aria-label={label}
      onKeyDown={onKey}
      className="flex gap-s1 overflow-x-auto"
      style={{ borderBottom: "1px solid var(--border-subtle)" }}
    >
      {items.map((t) => {
        const on = t.key === active;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={on}
            tabIndex={on ? 0 : -1}
            onClick={() => onChange(t.key)}
            className="inline-flex items-center gap-s2 whitespace-nowrap px-s4 py-s3 text-label font-semibold transition-colors duration-fast ease-standard hover:text-primary"
            style={{
              color: on ? "var(--accent)" : "var(--text-tertiary)",
              boxShadow: on ? "inset 0 -2px 0 0 var(--accent)" : undefined,
            }}
          >
            {t.icon && <Icon name={t.icon} size={15} />}
            {t.label}
            {t.badge !== undefined && t.badge > 0 && (
              <span
                className="rounded-pill px-s2 text-micro tabular-nums"
                style={{ background: "var(--state-late-soft)", color: "var(--state-late)" }}
              >
                {t.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

/* =====================================================================
   Dialog - 네이티브 <dialog>
   showModal() 이 포커스 트랩, Esc 닫기, top-layer, ::backdrop 을
   전부 공짜로 준다. 직접 구현하면 다 틀린다.

   들어오는 경로와 나가는 경로가 같다 (apple-design 7): 중앙에서
   scale 0.96 -> 1, 나갈 때 1 -> 0.96. 그래서 exit 를 위해 closing
   상태를 한 번 거친 뒤 close() 를 호출한다.
   ===================================================================== */

export const Dialog = ({
  open,
  onClose,
  title,
  children,
  footer,
  width = 480,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) => {
  const ref = useRef<HTMLDialogElement>(null);
  const [closing, setClosing] = useState(false);

  const finish = useCallback(() => {
    setClosing(false);
    ref.current?.close();
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) {
      if (prefersReducedMotion()) {
        finish();
      } else {
        setClosing(true);
        const t = setTimeout(finish, DURATION.base);
        return () => clearTimeout(t);
      }
    }
  }, [open, finish]);

  return (
    <dialog
      ref={ref}
      data-closing={closing || undefined}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
      className="jw-dialog jw-overlay-material p-0"
      style={{ width, maxWidth: "calc(100vw - 2rem)" }}
      aria-labelledby="jw-dialog-title"
    >
      <div className="flex flex-col gap-s4 p-s6">
        <div className="flex items-start justify-between gap-s4">
          <h2 id="jw-dialog-title" className="text-heading">
            {title}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} icon={<Icon name="x" size={16} />}>
            <span className="sr-only">닫기</span>
          </Button>
        </div>
        <div className="text-body" style={{ color: "var(--text-secondary)" }}>
          {children}
        </div>
        {footer && <div className="flex justify-end gap-s2">{footer}</div>}
      </div>
    </dialog>
  );
};

/** 파괴적 동작 확인. 되돌릴 수 없는 일에만 쓴다 (apple-design 16.2). */
export const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel = "확인",
  cancelLabel = "취소",
  busy = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  <Dialog
    open={open}
    onClose={onCancel}
    title={title}
    width={420}
    footer={
      <>
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={busy}>
          {cancelLabel}
        </Button>
        <Button variant="danger" size="sm" onClick={onConfirm} loading={busy}>
          {confirmLabel}
        </Button>
      </>
    }
  >
    {message}
  </Dialog>
);

/* =====================================================================
   Sheet - 아래에서 올라오는 시트. 진행현황 업데이트(1분 이내)용.
   여기가 모션 독트린이 실제로 다 쓰이는 유일한 곳이다.
     1:1 추적 -> 경계에서 러버밴딩 -> 릴리즈 시 관성 투사로 목표
     결정 -> 속도를 그대로 인계해 스프링 안착.
   그래서 애니메이션 라이브러리가 필요 없다.
   ===================================================================== */

export const Sheet = ({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}) => {
  const ref = useRef<HTMLDialogElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const drag = useRef({ startY: 0, y: 0, t: 0, v: 0, h: 0 });
  const anim = useRef<{ stop: () => void } | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
      if (panel.current) panel.current.style.transform = "";
    }
    if (!open && el.open) el.close();
  }, [open]);

  const setY = (y: number) => {
    if (panel.current) panel.current.style.transform = `translateY(${y}px)`;
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!panel.current) return;
    anim.current?.stop();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = {
      startY: e.clientY,
      y: 0,
      t: performance.now(),
      v: 0,
      h: panel.current.offsetHeight,
    };
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d.h) return;
    const raw = e.clientY - d.startY;
    // 위로는 갈 곳이 없다. 딱 멈추면 "얼었다"로 읽히므로 저항을 준다.
    const y = raw >= 0 ? raw : rubberband(raw, d.h);
    const now = performance.now();
    const dt = now - d.t;
    if (dt > 0) d.v = ((y - d.y) / dt) * 1000;
    d.y = y;
    d.t = now;
    setY(y);
  };

  const onPointerUp = () => {
    const d = drag.current;
    if (!d.h) return;
    // 릴리즈 지점이 아니라 "가고 있던 지점"으로 판정한다.
    const projected = d.y + project(d.v);
    const dismiss = projected > d.h * 0.4;
    anim.current = animateSpring({
      from: d.y,
      to: dismiss ? d.h : 0,
      velocity: d.v,
      spring: SPRING_SHEET,
      onFrame: setY,
      onRest: () => {
        if (dismiss) onClose();
      },
    });
    drag.current = { startY: 0, y: 0, t: 0, v: 0, h: 0 };
  };

  return (
    <dialog
      ref={ref}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
      className="jw-sheet p-0"
      aria-labelledby="jw-sheet-title"
    >
      <div
        ref={panel}
        className="flex flex-col rounded-t-overlay"
        style={{ background: "var(--surface-raised)", boxShadow: "var(--shadow-sheet)" }}
      >
        {/* 그래버. 여기만 드래그를 받는다. 본문을 드래그로 먹으면
            폼 안의 스크롤과 싸운다. */}
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className="flex cursor-grab touch-none justify-center py-s3 active:cursor-grabbing"
        >
          <span
            aria-hidden="true"
            className="h-1 w-10 rounded-pill"
            style={{ background: "var(--border-strong)" }}
          />
        </div>
        <div className="flex items-center justify-between gap-s4 px-s5 pb-s3">
          <h2 id="jw-sheet-title" className="text-heading">
            {title}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} icon={<Icon name="x" size={16} />}>
            <span className="sr-only">닫기</span>
          </Button>
        </div>
        <div className="flex flex-col gap-s4 px-s5 pb-s5">{children}</div>
        {footer && (
          <div
            className="flex justify-end gap-s2 px-s5 py-s4"
            style={{ borderTop: "1px solid var(--border-subtle)" }}
          >
            {footer}
          </div>
        )}
      </div>
    </dialog>
  );
};

/* =====================================================================
   Toast - <output> 은 기본이 ARIA live region 이다. div+role="status"
   가 필요 없고 Biome useSemanticElements 도 만족한다.
   ===================================================================== */

export const Toast = ({
  message,
  tone = "success",
  action,
}: {
  message: string | null;
  tone?: "success" | "error";
  /**
   * 되돌리기 같은 즉시 취소 경로. 확인 다이얼로그를 쓰지 않고 완료를
   * 실행하기 때문에(결정 S-10) 토스트가 취소 진입점을 갖는다.
   */
  action?: ReactNode;
}) => {
  if (!message) return null;
  const err = tone === "error";
  return (
    <output
      className="jw-toast fixed bottom-s8 left-1/2 z-toast inline-flex -translate-x-1/2 items-center gap-s3 rounded-overlay px-s5 py-s3 text-label font-semibold"
      style={{
        background: "var(--surface-raised)",
        color: "var(--text-primary)",
        boxShadow: "var(--shadow-overlay)",
      }}
    >
      <Icon
        name={err ? "alert" : "check"}
        size={15}
        stroke={2.4}
        color={err ? "var(--state-late)" : "var(--state-normal)"}
      />
      {message}
      {action}
    </output>
  );
};

/* =====================================================================
   Tooltip - hover 와 focus 모두에서 뜬다. aria-describedby 로
   연결되므로 스크린리더도 읽는다. transform-origin 이 트리거를
   향하므로 어디서 나왔는지 보인다 (apple-design 7).
   ===================================================================== */

export const Tooltip = ({
  content,
  children,
  side = "top",
}: {
  content: string;
  children: ReactNode;
  side?: "top" | "bottom";
}) => {
  const id = useId();
  const [on, setOn] = useState(false);
  return (
    <span
      className="relative inline-flex"
      onPointerEnter={() => setOn(true)}
      onPointerLeave={() => setOn(false)}
      onFocus={() => setOn(true)}
      onBlur={() => setOn(false)}
    >
      <span aria-describedby={id}>{children}</span>
      <span
        id={id}
        role="tooltip"
        hidden={!on}
        className={[
          "jw-pop pointer-events-none absolute left-1/2 z-popover w-max max-w-[16rem] -translate-x-1/2 rounded-control px-s3 py-s1 text-caption",
          side === "top" ? "bottom-full mb-s2 origin-bottom" : "top-full mt-s2 origin-top",
        ].join(" ")}
        style={{
          background: "var(--surface-raised)",
          color: "var(--text-primary)",
          boxShadow: "var(--shadow-overlay)",
        }}
      >
        {content}
      </span>
    </span>
  );
};

/* =====================================================================
   Skeleton / EmptyState / ErrorState / LoadingState
   ===================================================================== */

export const Skeleton = ({
  w = "100%",
  h = 14,
  className,
}: {
  w?: number | string;
  h?: number | string;
  className?: string;
}) => (
  <span
    aria-hidden="true"
    className={["jw-skeleton block", className ?? ""].join(" ")}
    style={{ width: w, height: h }}
  />
);

/** 최종 레이아웃과 같은 모양으로 기다린다. 원형 스피너를 쓰지 않는다. */
export const TableSkeleton = ({ rows = 8, cols = 6 }: { rows?: number; cols?: number }) => (
  <div aria-busy="true" aria-live="polite" className="flex flex-col gap-s3 p-s4">
    <span className="sr-only">불러오는 중</span>
    {Array.from({ length: rows }, (_, r) => (
      <div key={r} className="flex gap-s4">
        {Array.from({ length: cols }, (_, c) => (
          <Skeleton key={c} w={c === 0 ? "22%" : "13%"} h={12} />
        ))}
      </div>
    ))}
  </div>
);

export const EmptyState = ({
  title,
  description,
  action,
  icon = "list",
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: IconName;
}) => (
  <div className="flex flex-col items-center justify-center gap-s3 px-s5 py-s10 text-center">
    <span style={{ color: "var(--text-quaternary)" }}>
      <Icon name={icon} size={28} stroke={1.6} />
    </span>
    <p className="text-heading">{title}</p>
    {description && (
      <p className="max-w-[28rem] text-label" style={{ color: "var(--text-tertiary)" }}>
        {description}
      </p>
    )}
    {action}
  </div>
);

export const ErrorState = ({ error, onRetry }: { error: Error; onRetry?: () => void }) => (
  <div className="flex flex-col items-center justify-center gap-s3 px-s5 py-s9 text-center">
    <span style={{ color: "var(--state-late)" }}>
      <Icon name="alert" size={26} />
    </span>
    <p className="text-heading">데이터를 불러오지 못했습니다</p>
    <p className="max-w-[28rem] text-label" style={{ color: "var(--text-tertiary)" }}>
      {error.message}
    </p>
    {onRetry && (
      <Button
        variant="secondary"
        size="sm"
        icon={<Icon name="history" size={14} />}
        onClick={onRetry}
      >
        다시 시도
      </Button>
    )}
  </div>
);

export const LoadingState = ({ label = "불러오는 중" }: { label?: string }) => (
  <div
    aria-busy="true"
    aria-live="polite"
    className="flex flex-col items-center justify-center gap-s3 py-s9 text-label"
    style={{ color: "var(--text-tertiary)" }}
  >
    <Spinner size={22} />
    {label}
  </div>
);

export const DataBoundary = ({
  loading,
  error,
  onRetry,
  skeleton,
  children,
}: {
  loading: boolean;
  error: Error | null;
  onRetry?: () => void;
  /** 있으면 스피너 대신 형태를 맞춘 스켈레톤을 쓴다 */
  skeleton?: ReactNode;
  children: ReactNode;
}) => {
  if (loading) return <>{skeleton ?? <LoadingState />}</>;
  if (error) return <ErrorState error={error} onRetry={onRetry} />;
  return <>{children}</>;
};

/* =====================================================================
   Pagination
   ===================================================================== */

export const Pagination = ({
  page,
  pageCount,
  onChange,
}: {
  /** 1-based */
  page: number;
  pageCount: number;
  onChange: (p: number) => void;
}) => {
  if (pageCount <= 1) return null;
  // 현재 페이지 주변 5개 창.
  const from = Math.max(1, Math.min(page - 2, pageCount - 4));
  const pages = Array.from({ length: Math.min(5, pageCount) }, (_, i) => from + i);
  return (
    <nav aria-label="페이지" className="flex items-center justify-center gap-s1">
      <Button
        variant="ghost"
        size="sm"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        icon={<Icon name="chevL" size={14} />}
      >
        <span className="sr-only">이전 페이지</span>
      </Button>
      {pages.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onChange(p)}
          aria-current={p === page ? "page" : undefined}
          className="h-control-sm min-w-control-sm rounded-control px-s2 text-caption font-semibold tabular-nums transition-colors duration-fast ease-standard hover:bg-overlay-hover"
          style={
            p === page
              ? { background: "var(--accent)", color: "var(--text-on-accent)" }
              : { color: "var(--text-secondary)" }
          }
        >
          {p}
        </button>
      ))}
      <Button
        variant="ghost"
        size="sm"
        disabled={page >= pageCount}
        onClick={() => onChange(page + 1)}
        icon={<Icon name="chevR" size={14} />}
      >
        <span className="sr-only">다음 페이지</span>
      </Button>
    </nav>
  );
};

/* =====================================================================
   FilterBar - 계획서 4 의 조회 조건 묶음
   sticky 반투명 크롬. 밑으로 테이블이 흐른다.
   ===================================================================== */

export const FilterBar = ({
  children,
  right,
  sticky = true,
}: {
  children: ReactNode;
  right?: ReactNode;
  sticky?: boolean;
}) => (
  <div
    className={[
      "flex flex-wrap items-center gap-s3 px-s4 py-s3",
      sticky ? "jw-chrome sticky top-0 z-chrome" : "",
    ].join(" ")}
  >
    <span style={{ color: "var(--text-tertiary)" }}>
      <Icon name="filter" size={14} />
    </span>
    {children}
    {right && <span className="ml-auto flex items-center gap-s2">{right}</span>}
  </div>
);
